import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Mail, Phone, Briefcase, FileText, MessageSquare, CheckCircle2, Loader2, Trash2, Plus, Pencil, X, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statuses = ["Open", "Contacted", "Quotation Sent", "Approved"];
const statusConfig = {
  Open: { bg: "bg-blue-50", text: "text-blue-700" },
  Contacted: { bg: "bg-yellow-50", text: "text-yellow-700" },
  "Quotation Sent": { bg: "bg-purple-50", text: "text-purple-700" },
  Approved: { bg: "bg-green-50", text: "text-green-700" },
};

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

const emptyRow = () => ({ item: "", description: "", cost: 0, tax: 0, total: 0 });

function QuotationForm({ initialItems, onSubmit, onCancel, submitLabel, submitting }) {
  const [items, setItems] = useState(initialItems && initialItems.length > 0 ? initialItems.map(i => ({ ...i })) : [emptyRow()]);

  const updateItem = (idx, field, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    if (field === "cost" || field === "tax") {
      const cost = parseFloat(field === "cost" ? value : next[idx].cost) || 0;
      const tax = parseFloat(field === "tax" ? value : next[idx].tax) || 0;
      next[idx].total = parseFloat((cost + (cost * tax / 100)).toFixed(2));
    }
    setItems(next);
  };

  const addRow = () => setItems([...items, emptyRow()]);
  const removeRow = (idx) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };

  const grandTotal = items.reduce((sum, i) => sum + (parseFloat(i.total) || 0), 0);

  return (
    <div className="space-y-4" data-testid="quotation-form">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-[#D9D7D0]">
              <th className="px-3 py-2 text-xs font-bold text-[#5A5955] w-10">#</th>
              <th className="px-3 py-2 text-xs font-bold text-[#5A5955]">Item</th>
              <th className="px-3 py-2 text-xs font-bold text-[#5A5955]">Description</th>
              <th className="px-3 py-2 text-xs font-bold text-[#5A5955] w-28">Cost</th>
              <th className="px-3 py-2 text-xs font-bold text-[#5A5955] w-24">Tax %</th>
              <th className="px-3 py-2 text-xs font-bold text-[#5A5955] w-28">Total</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, idx) => (
              <tr key={idx} className="border-b border-[#D9D7D0]" data-testid={`quotation-row-${idx}`}>
                <td className="px-3 py-2 text-xs text-[#5A5955]">{idx + 1}</td>
                <td className="px-3 py-2">
                  <input value={row.item} onChange={(e) => updateItem(idx, "item", e.target.value)} data-testid={`q-item-${idx}`} className="w-full bg-[#F7F6F2] border-b border-black/10 focus:border-[#161616] outline-none px-2 py-1.5 text-sm" placeholder="Item name" />
                </td>
                <td className="px-3 py-2">
                  <input value={row.description} onChange={(e) => updateItem(idx, "description", e.target.value)} data-testid={`q-desc-${idx}`} className="w-full bg-[#F7F6F2] border-b border-black/10 focus:border-[#161616] outline-none px-2 py-1.5 text-sm" placeholder="Description" />
                </td>
                <td className="px-3 py-2">
                  <input type="number" value={row.cost} onChange={(e) => updateItem(idx, "cost", e.target.value)} data-testid={`q-cost-${idx}`} className="w-full bg-[#F7F6F2] border-b border-black/10 focus:border-[#161616] outline-none px-2 py-1.5 text-sm text-right" min="0" step="0.01" />
                </td>
                <td className="px-3 py-2">
                  <input type="number" value={row.tax} onChange={(e) => updateItem(idx, "tax", e.target.value)} data-testid={`q-tax-${idx}`} className="w-full bg-[#F7F6F2] border-b border-black/10 focus:border-[#161616] outline-none px-2 py-1.5 text-sm text-right" min="0" step="0.01" />
                </td>
                <td className="px-3 py-2 text-sm font-medium text-[#161616] text-right">{(parseFloat(row.total) || 0).toFixed(2)}</td>
                <td className="px-2 py-2">
                  {items.length > 1 && (
                    <button onClick={() => removeRow(idx)} data-testid={`q-remove-${idx}`} className="p-1 hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors">
                      <X size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button onClick={addRow} data-testid="add-more-row-btn" className="inline-flex items-center gap-1 text-xs font-bold text-[#161616] hover:text-[#00739E] transition-colors">
          <Plus size={14} /> Add More
        </button>
        <div className="text-right">
          <span className="text-xs font-bold text-[#5A5955]">Grand Total: </span>
          <span className="text-lg font-black text-[#161616] tracking-tight">{grandTotal.toFixed(2)}</span>
        </div>
      </div>

      <div className="flex gap-3 pt-2 border-t border-[#D9D7D0]">
        <button onClick={onCancel} data-testid="cancel-quotation-btn" className="px-5 py-2.5 text-xs font-bold text-[#5A5955] border border-[#D9D7D0] hover:bg-[#F4F3EF] transition-colors">
          Cancel
        </button>
        <button
          onClick={() => onSubmit(items)}
          disabled={submitting || items.every(i => !i.item.trim())}
          data-testid="submit-quotation-btn"
          className="bg-[#161616] text-white font-bold px-5 py-2.5 text-xs hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#00A3E0] transition-all duration-200 disabled:opacity-50"
        >
          {submitting ? "Saving..." : submitLabel}
        </button>
      </div>
    </div>
  );
}

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [quotations, setQuotations] = useState([]);
  const [showAddQuotation, setShowAddQuotation] = useState(false);
  const [showUpdateQuotation, setShowUpdateQuotation] = useState(false);
  const [qSubmitting, setQSubmitting] = useState(false);
  const [leadManagers, setLeadManagers] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const fetchLead = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leads/${leadId}`, { headers: getAuthHeaders(), withCredentials: true });
      setLead(data);
      setEditForm({ requirements: data.requirements || "", remarks: data.remarks || "" });
    } catch (err) { console.error("Failed to fetch lead", err); }
    finally { setLoading(false); }
  }, [leadId]);

  const fetchQuotations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/leads/${leadId}/quotations`, { headers: getAuthHeaders(), withCredentials: true });
      setQuotations(data);
    } catch (err) { console.error("Failed to fetch quotations", err); }
  }, [leadId]);

  const fetchLeadManagers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/staff/by-role/Lead Manager`, { headers: getAuthHeaders(), withCredentials: true });
      setLeadManagers(data);
    } catch (err) { console.error("Failed to fetch lead managers", err); }
  }, []);

  useEffect(() => { fetchLead(); fetchQuotations(); fetchLeadManagers(); }, [fetchLead, fetchQuotations, fetchLeadManagers]);

  const assignLead = async (staffId, staffName) => {
    setAssigning(true);
    try {
      const { data } = await axios.put(`${API}/leads/${leadId}/assign`, { assigned_to_id: staffId, assigned_to_name: staffName }, { headers: getAuthHeaders(), withCredentials: true });
      setLead(data);
    } catch (err) { console.error(err); }
    finally { setAssigning(false); }
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      const { data } = await axios.put(`${API}/leads/${leadId}`, { status: newStatus }, { headers: getAuthHeaders(), withCredentials: true });
      setLead(data);
    } catch (err) { console.error(err); }
    finally { setUpdating(false); }
  };

  const saveEdit = async () => {
    setUpdating(true);
    try {
      const { data } = await axios.put(`${API}/leads/${leadId}`, editForm, { headers: getAuthHeaders(), withCredentials: true });
      setLead(data);
      setEditing(false);
    } catch (err) { console.error(err); }
    finally { setUpdating(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, { headers: getAuthHeaders(), withCredentials: true });
      navigate("/admin/leads");
    } catch (err) { console.error(err); }
  };

  const handleAddQuotation = async (items) => {
    setQSubmitting(true);
    try {
      await axios.post(`${API}/leads/${leadId}/quotations`, { items }, { headers: getAuthHeaders(), withCredentials: true });
      setShowAddQuotation(false);
      fetchQuotations();
    } catch (err) { console.error(err); }
    finally { setQSubmitting(false); }
  };

  const handleUpdateQuotation = async (items) => {
    setQSubmitting(true);
    try {
      await axios.put(`${API}/leads/${leadId}/quotations/latest`, { items }, { headers: getAuthHeaders(), withCredentials: true });
      setShowUpdateQuotation(false);
      fetchQuotations();
    } catch (err) { console.error(err); }
    finally { setQSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#F4F3EF] flex items-center justify-center"><Loader2 className="animate-spin text-[#161616]" size={32} /></div>;
  if (!lead) return <div className="min-h-screen bg-[#F4F3EF] flex items-center justify-center"><p className="text-[#5A5955]">Lead not found.</p></div>;

  const currentIndex = statuses.indexOf(lead.status);
  const sc = statusConfig[lead.status] || statusConfig.Open;
  const latestQuotation = quotations.length > 0 ? quotations[0] : null;

  return (
    <div data-testid="lead-detail-page" className="min-h-screen bg-[#F4F3EF]">
      {/* Header */}
      <div className="bg-white border-b border-[#161616] px-5 sm:px-8 py-6 gap-4 flex-wrap flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/admin/leads")} data-testid="back-to-leads" className="p-2 hover:bg-[#F4F3EF] transition-colors">
            <ArrowLeft size={20} className="text-[#161616]" />
          </button>
          <div>
            <h1 className="text-3xl font-bold tracking-[-0.02em] text-[#161616]">{lead.name}</h1>
            <span className={`inline-block mt-1 px-3 py-0.5 text-xs font-bold ${sc.bg} ${sc.text}`}>{lead.status}</span>
          </div>
        </div>
        <button onClick={handleDelete} data-testid="delete-lead-detail" className="inline-flex items-center gap-2 px-4 py-2 text-sm text-red-600 border border-red-200 hover:bg-red-50 transition-colors font-medium">
          <Trash2 size={14} /> Delete
        </button>
      </div>

      <div className="px-8 py-8 space-y-8 max-w-5xl">
        {/* Status Pipeline */}
        <div className="bg-white border border-[#D9D7D0] p-6" data-testid="status-pipeline">
          <p className="text-xs font-bold text-[#5A5955] mb-4">Status Pipeline</p>
          <div className="flex items-center gap-0">
            {statuses.map((s, i) => {
              const isActive = i <= currentIndex;
              const isCurrent = s === lead.status;
              const conf = statusConfig[s];
              return (
                <button key={s} onClick={() => updateStatus(s)} disabled={updating} data-testid={`status-btn-${s.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flex-1 py-3 px-2 text-xs font-bold text-center transition-all border ${isCurrent ? `${conf.bg} ${conf.text} border-current` : isActive ? "bg-[#F4F3EF] text-[#161616] border-[#D9D7D0]" : "bg-white text-[#5A5955]/50 border-[#D9D7D0] hover:bg-[#F7F6F2]"} ${i === 0 ? "" : "border-l-0"}`}>
                  {isCurrent && <CheckCircle2 size={12} className="inline mr-1 -mt-0.5" />}{s}
                </button>
              );
            })}
          </div>
        </div>

        {/* Assign to Lead Manager */}
        <div className="bg-white border border-[#D9D7D0] p-6" data-testid="lead-assign-section">
          <p className="text-xs font-bold text-[#5A5955] mb-4">Assign to Lead Manager</p>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 flex-1">
              <UserCheck size={16} className="text-[#5A5955]" />
              {lead.assigned_to_name ? (
                <span className="text-sm font-medium text-[#161616]">{lead.assigned_to_name}</span>
              ) : (
                <span className="text-sm text-[#5A5955]">Not assigned</span>
              )}
            </div>
            <select
              value={lead.assigned_to_id || ""}
              onChange={(e) => {
                const selected = leadManagers.find(m => m.id === e.target.value);
                if (selected) assignLead(selected.id, selected.name);
              }}
              disabled={assigning}
              data-testid="assign-lead-manager"
              className="bg-[#F7F6F2] border border-[#D9D7D0] focus:border-[#161616] outline-none px-3 py-2 text-sm transition-colors min-w-[200px]"
            >
              <option value="">{lead.assigned_to_name ? "Change assignment" : "Select Lead Manager"}</option>
              {leadManagers.map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Lead Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white border border-[#D9D7D0] p-6 space-y-4">
            <p className="text-xs font-bold text-[#5A5955]">Contact Information</p>
            <div className="space-y-3">
              <div className="flex items-center gap-3"><Mail size={16} className="text-[#5A5955]" /><span className="text-sm text-[#161616]" data-testid="lead-email">{lead.email}</span></div>
              <div className="flex items-center gap-3"><Phone size={16} className="text-[#5A5955]" /><span className="text-sm text-[#161616]" data-testid="lead-phone">{lead.phone || "Not provided"}</span></div>
              <div className="flex items-center gap-3"><Briefcase size={16} className="text-[#5A5955]" /><span className="text-sm text-[#161616]" data-testid="lead-service">{lead.service || "Not specified"}</span></div>
            </div>
          </div>
          <div className="bg-white border border-[#D9D7D0] p-6 space-y-4">
            <p className="text-xs font-bold text-[#5A5955]">Timeline</p>
            <div className="space-y-2 text-sm text-[#5A5955]">
              <p>Created: {new Date(lead.created_at).toLocaleString()}</p>
              <p>Updated: {new Date(lead.updated_at).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Requirements & Remarks */}
        <div className="bg-white border border-[#D9D7D0] p-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-[#5A5955]">Requirements & Remarks</p>
            {!editing && <button onClick={() => setEditing(true)} data-testid="edit-lead-btn" className="text-xs font-bold text-[#161616] hover:underline">Edit</button>}
          </div>
          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#5A5955] mb-1">Requirements</label>
                <textarea rows={3} value={editForm.requirements} onChange={(e) => setEditForm({...editForm, requirements: e.target.value})} data-testid="edit-requirements" className="field resize-y" />
              </div>
              <div>
                <label className="block text-sm text-[#5A5955] mb-1">Remarks</label>
                <textarea rows={2} value={editForm.remarks} onChange={(e) => setEditForm({...editForm, remarks: e.target.value})} data-testid="edit-remarks" className="field resize-y" />
              </div>
              <div className="flex gap-3">
                <button onClick={saveEdit} disabled={updating} data-testid="save-edit-btn" className="bg-[#161616] text-white font-bold px-5 py-2.5 text-xs hover:-translate-y-0.5 transition-all disabled:opacity-50">{updating ? "Saving..." : "Save"}</button>
                <button onClick={() => setEditing(false)} className="px-5 py-2.5 text-xs font-bold text-[#5A5955] border border-[#D9D7D0] hover:bg-[#F4F3EF] transition-colors">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-1"><FileText size={14} className="text-[#5A5955]" /><span className="text-xs font-bold text-[#5A5955]">Requirements</span></div>
                <p className="text-sm text-[#161616] whitespace-pre-wrap" data-testid="lead-requirements">{lead.requirements || "No requirements specified."}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1"><MessageSquare size={14} className="text-[#5A5955]" /><span className="text-xs font-bold text-[#5A5955]">Remarks</span></div>
                <p className="text-sm text-[#161616] whitespace-pre-wrap" data-testid="lead-remarks">{lead.remarks || "No remarks."}</p>
              </div>
            </div>
          )}
        </div>

        {/* Quotations Section */}
        <div className="bg-white border border-[#D9D7D0] p-6 space-y-6" data-testid="quotations-section">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-[#5A5955]">Quotations ({quotations.length})</p>
            <div className="flex gap-2">
              {latestQuotation && !showUpdateQuotation && !showAddQuotation && (
                <button onClick={() => { setShowUpdateQuotation(true); setShowAddQuotation(false); }} data-testid="update-quotation-btn"
                  className="inline-flex items-center gap-1 px-4 py-2 text-xs font-bold text-[#161616] border border-[#D9D7D0] hover:border-[#161616] transition-colors">
                  <Pencil size={12} /> Update Last Quotation
                </button>
              )}
              {!showAddQuotation && !showUpdateQuotation && (
                <button onClick={() => { setShowAddQuotation(true); setShowUpdateQuotation(false); }} data-testid="add-quotation-btn"
                  className="inline-flex items-center gap-1 bg-[#161616] text-white font-bold px-4 py-2 text-xs hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#00A3E0] transition-all duration-200">
                  <Plus size={14} /> Add Quotation
                </button>
              )}
            </div>
          </div>

          {/* Add Quotation Form */}
          {showAddQuotation && (
            <div className="border border-[#D9D7D0] p-6 bg-[#F7F6F2]" data-testid="add-quotation-form-wrapper">
              <p className="text-sm font-bold text-[#161616] mb-4">Create Quotation</p>
              <QuotationForm initialItems={null} onSubmit={handleAddQuotation} onCancel={() => setShowAddQuotation(false)} submitLabel="Create Quotation" submitting={qSubmitting} />
            </div>
          )}

          {/* Update Last Quotation Form */}
          {showUpdateQuotation && latestQuotation && (
            <div className="border border-[#D9D7D0] p-6 bg-[#F7F6F2]" data-testid="update-quotation-form-wrapper">
              <p className="text-sm font-bold text-[#161616] mb-4">Update Last Quotation</p>
              <QuotationForm initialItems={latestQuotation.items} onSubmit={handleUpdateQuotation} onCancel={() => setShowUpdateQuotation(false)} submitLabel="Update Quotation" submitting={qSubmitting} />
            </div>
          )}

          {/* Quotation list */}
          {quotations.length === 0 && !showAddQuotation ? (
            <div className="text-center py-8 border border-dashed border-[#D9D7D0]">
              <FileText size={28} className="mx-auto text-[#D9D7D0] mb-2" />
              <p className="text-sm text-[#5A5955]">No quotations yet. Create one to get started.</p>
            </div>
          ) : (
            quotations.map((q, qi) => (
              <div key={q.id} className="border border-[#D9D7D0] bg-[#F7F6F2]" data-testid={`quotation-card-${qi}`}>
                <div className="flex items-center justify-between px-6 py-3 border-b border-[#D9D7D0] bg-white">
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-bold text-[#5A5955]">Quotation #{quotations.length - qi}</span>
                    {qi === 0 && <span className="text-xs font-bold bg-[#00A3E0]/10 text-[#00739E] px-2 py-0.5">Latest</span>}
                  </div>
                  <span className="text-xs text-[#5A5955]">{new Date(q.created_at).toLocaleDateString()}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-[#D9D7D0]">
                        <th className="px-6 py-2 text-xs font-bold text-[#5A5955] w-10">#</th>
                        <th className="px-4 py-2 text-xs font-bold text-[#5A5955]">Item</th>
                        <th className="px-4 py-2 text-xs font-bold text-[#5A5955]">Description</th>
                        <th className="px-4 py-2 text-xs font-bold text-[#5A5955] text-right">Cost</th>
                        <th className="px-4 py-2 text-xs font-bold text-[#5A5955] text-right">Tax %</th>
                        <th className="px-4 py-2 text-xs font-bold text-[#5A5955] text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.items.map((item, ii) => (
                        <tr key={ii} className="border-b border-[#D9D7D0]">
                          <td className="px-6 py-2 text-xs text-[#5A5955]">{ii + 1}</td>
                          <td className="px-4 py-2 text-sm text-[#161616] font-medium">{item.item}</td>
                          <td className="px-4 py-2 text-sm text-[#5A5955]">{item.description || "—"}</td>
                          <td className="px-4 py-2 text-sm text-[#161616] text-right">{parseFloat(item.cost).toFixed(2)}</td>
                          <td className="px-4 py-2 text-sm text-[#161616] text-right">{parseFloat(item.tax).toFixed(2)}%</td>
                          <td className="px-4 py-2 text-sm text-[#161616] font-medium text-right">{parseFloat(item.total).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-6 py-3 border-t border-[#D9D7D0] bg-white flex justify-end">
                  <span className="text-xs font-bold text-[#5A5955] mr-3">Grand Total:</span>
                  <span className="text-base font-black text-[#161616] tracking-tight">{parseFloat(q.grand_total).toFixed(2)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
